import { RateBookTable } from "@/components/data-table/rate-book-table";
import { PageHeader } from "@/components/layout/page-header";
import { CurrentRateBookHeader } from "@/components/rates/current-rate-book-header";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { LoadingState } from "@/components/ui/loading-state";
import { getFiles, getRates } from "@/services/api";
import { useTabActions } from "@/stores/tab-store";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Страница книги расценок — US2.
 * В системе доступна одна активная книга: страница автоматически
 * показывает текущий файл и таблицу расценок без ручного выбора.
 */
export function RatesPage() {
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const { openUploadTab } = useTabActions();

	const { data: filesData, isLoading: isFilesLoading } = useQuery({
		queryKey: ["files", { type: "rate_book" }],
		queryFn: () => getFiles({ type: "rate_book" }),
	});

	const rateBookFiles = useMemo(() => {
		const files = filesData?.files ?? [];
		return [...files].sort(
			(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
		);
	}, [filesData?.files]);

	const currentRateBook = rateBookFiles[0] ?? null;
	const currentRateBookId = currentRateBook?.id;
	const legacyRateBookCount = Math.max(rateBookFiles.length - 1, 0);
	const previousRateBookIdRef = useRef<number | null>(null);

	useEffect(() => {
		const normalizedId = currentRateBookId ?? null;
		if (previousRateBookIdRef.current !== normalizedId) {
			previousRateBookIdRef.current = normalizedId;
			setSearch("");
			setPage(1);
		}
	}, [currentRateBookId]);

	const { data: ratesData, isLoading: isRatesLoading } = useQuery({
		queryKey: ["rates", currentRateBookId ?? null, search, page],
		queryFn: () => {
			if (!currentRateBookId) {
				throw new Error("Rate book is not selected");
			}
			return getRates(currentRateBookId, search, page);
		},
		enabled: !!currentRateBookId,
	});

	const handleSearchChange = useCallback((value: string) => {
		setSearch(value);
		setPage(1);
	}, []);

	return (
		<div className="space-y-6">
			<PageHeader
				title="Книга расценок"
				subtitle="В системе хранится одна активная книга. Ниже отображаются её параметры и расценки."
			/>

			{isFilesLoading ? (
				<LoadingState message="Загрузка информации о книге расценок..." />
			) : currentRateBook ? (
				<CurrentRateBookHeader
					file={currentRateBook}
					totalRates={ratesData?.total ?? 0}
					legacyCount={legacyRateBookCount}
				/>
			) : (
				<EmptyState
					title="Книга расценок ещё не загружена"
					description="Перейдите в раздел «Файлы», чтобы загрузить книгу."
					action={
						<Button type="button" variant="outline" onClick={openUploadTab}>
							Открыть раздел «Файлы»
						</Button>
					}
				/>
			)}

			{/* Rate book table */}
			{currentRateBook ? (
				<RateBookTable
					rates={ratesData?.rates ?? []}
					total={ratesData?.total ?? 0}
					page={ratesData?.page ?? 1}
					pageSize={ratesData?.pageSize ?? 50}
					search={search}
					onSearchChange={handleSearchChange}
					onPageChange={setPage}
					isLoading={isRatesLoading}
				/>
			) : (
				<div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-slate-500">
					После загрузки книги расценок здесь появится таблица.
				</div>
			)}
		</div>
	);
}
